import { useMemo } from 'react';
import { CAS, VDMS, DSS, CREATED_TIMESTAMP, REQUEST_TYPE_MAPPING, DAS } from '../constants';
import { timestampIntegrators } from '../timestampIntegrators';
import {
  resolveSupplySourcePodDetailsResolver,
  resolveSupplySourceFullContentMagicWords,
} from './useSupplySourcePodDetailsResolver';

const FIELDS_NAMES = {
  TIMESTAMP: 'Timestamp',
  TYPE: 'Supply Source',
  APP_NAME: 'App Name',
  PUBLISHER: 'Publisher',
  CHANNEL: 'Channel',
  DEVICE_TYPE: 'Device Type',
  BRAND: 'Brand',
  FORM_FACTOR: 'Form Factor',
  DEVICE_CATEGORY: 'Category',
  VDM: 'VDM',
  REQUEST_TYPE: 'Request Type',
  REQUEST_ID: 'Request ID',
  SESSION_ID: 'Session ID',
  DEVICE_NAME: 'Device Name',
  DISTRIBUTOR: 'Distributor',
  DISTRIBUTION_PARTNER: 'Distribution Partner',
  PLAYBACK_TYPE: 'Playback Type',
  IPV6: 'Device IPv6',
  IPV4: 'Device IPv4',
  IFA: 'Device IFA',
  IFA_TYPE: 'Device IFA Type',
  DEVICE_USER_AGENT: 'Device User Agent',
  LIMITED_AD_TRACKING: 'Limit Ad Tracking',
  ENTITY_TYPE: 'Entity Type',
  FULL_CONTENT_MAGIC_WORDS: 'Full Content Magic Words',
  EXPERIMENT_ID: 'Experiment ID',
  ENTITLEMENT: 'Entitlement',
  COUNTRY: 'Country',
  STATE: 'State',
  CITY: 'City',
  POSTAL_CODE: 'Postal Code',
  DMA_CODE: 'DMA Code',
  TIME_ZONE: 'Time Zone',
};

const DEFAULT_SUPPLY_SOURCE_SUMMARY = [
  { label: FIELDS_NAMES.TIMESTAMP, value: '-' },
  { label: FIELDS_NAMES.TYPE, value: '-' },
  { label: FIELDS_NAMES.REQUEST_TYPE, value: '-' },
  { label: FIELDS_NAMES.REQUEST_ID, value: '-' },
  { label: FIELDS_NAMES.SESSION_ID, value: '-' },
  { label: FIELDS_NAMES.EXPERIMENT_ID, value: '-' },
  { label: FIELDS_NAMES.APP_NAME, value: '-' },
  { label: FIELDS_NAMES.PUBLISHER, value: '-' },
  { label: FIELDS_NAMES.BRAND, value: '-' },
];

const convertVDM = (data) => {
  try {
    if (data?.toString().includes('1')) return 'live';
    if (data?.toString().includes('0')) return 'lfv';
  } catch (error) {
    console.error(`convertVDM, ${error}`);
    throw new Error('Data is not available!');
  }
};

const vdmsSupplySourceSummary = (data) => {
  const { sessionid, devicetype, ip, ifa, ifatype, ua, lmt } = data?.rawData?.device;
  return [
    { label: FIELDS_NAMES.TIMESTAMP, value: timestampIntegrators(data?.[CREATED_TIMESTAMP]) },
    { label: FIELDS_NAMES.TYPE, value: VDMS },
    { label: FIELDS_NAMES.REQUEST_TYPE, value: REQUEST_TYPE_MAPPING[VDMS] },
    { label: FIELDS_NAMES.REQUEST_ID, value: '-' },
    { label: FIELDS_NAMES.SESSION_ID, value: sessionid },
    { label: FIELDS_NAMES.EXPERIMENT_ID, value: data?.rawData?.['experiment-id'] },
    { label: FIELDS_NAMES.APP_NAME, value: data?.rawData?.app.name },
    { label: FIELDS_NAMES.PUBLISHER, value: data?.rawData?.app.publisher?.name || '-' },
    { label: FIELDS_NAMES.DISTRIBUTOR, value: '-' },
    { label: FIELDS_NAMES.PLAYBACK_TYPE, value: convertVDM(data?.rawData?.content?.livestream) },
    { label: FIELDS_NAMES.ENTITY_TYPE, value: '-' },
    { label: FIELDS_NAMES.DEVICE_NAME, value: devicetype },
    { label: FIELDS_NAMES.BRAND, value: '-' },
    { label: FIELDS_NAMES.FORM_FACTOR, value: '-' },
    { label: FIELDS_NAMES.DEVICE_CATEGORY, value: '-' },
    { label: FIELDS_NAMES.IPV6, value: ip },
    { label: FIELDS_NAMES.IPV4, value: ip },
    { label: FIELDS_NAMES.IFA, value: ifa },
    { label: FIELDS_NAMES.IFA_TYPE, value: ifatype },
    { label: FIELDS_NAMES.DEVICE_USER_AGENT, value: ua },
    { label: FIELDS_NAMES.LIMITED_AD_TRACKING, value: lmt },
  ];
};

const dssSupplySourceSummary = (data) => [
  { label: FIELDS_NAMES.TIMESTAMP, value: timestampIntegrators(data?.[CREATED_TIMESTAMP]) },
  { label: FIELDS_NAMES.TYPE, value: DSS },
  { label: FIELDS_NAMES.REQUEST_TYPE, value: REQUEST_TYPE_MAPPING[DSS] },
  { label: FIELDS_NAMES.REQUEST_ID, value: '-' },
  { label: FIELDS_NAMES.SESSION_ID, value: data?.rawData?.device?.sessionid },
  { label: FIELDS_NAMES.EXPERIMENT_ID, value: data?.rawData?.['experiment-id'] },
  { label: FIELDS_NAMES.APP_NAME, value: data?.rawData?.app.name },
  { label: FIELDS_NAMES.DISTRIBUTOR, value: data?.rawData?.content?.distributor || '-' },
  { label: FIELDS_NAMES.PLAYBACK_TYPE, value: data?.rawData?.app?.content?.livestream },
  { label: FIELDS_NAMES.ENTITY_TYPE, value: data?.rawData?.content?.['entity-type'] || '-' },
  { label: FIELDS_NAMES.DEVICE_NAME, value: data?.rawData?.device?.ext?.devicetype },
  { label: FIELDS_NAMES.BRAND, value: data?.rawData?.device?.model || '-' },
  { label: FIELDS_NAMES.FORM_FACTOR, value: '' },
  { label: FIELDS_NAMES.DEVICE_CATEGORY, value: '-' },
  { label: FIELDS_NAMES.IPV6, value: data?.rawData?.device?.ip },
  { label: FIELDS_NAMES.IPV4, value: data?.rawData?.device?.ip },
  { label: FIELDS_NAMES.IFA, value: data?.rawData?.device?.ifa },
  { label: FIELDS_NAMES.IFA_TYPE, value: data?.rawData?.device?.ext?.ifatype },
  { label: FIELDS_NAMES.DEVICE_USER_AGENT, value: data?.rawData?.device?.ua },
  { label: FIELDS_NAMES.LIMITED_AD_TRACKING, value: data?.rawData?.device?.lmt },
];
const casSupplySourceSummary = (data) => {
  const contentDataV2 = data?.rawData?.['content-info'];
  const contentDataV3 = data?.rawData?.content;
  const deviceDataV2 = data?.rawData?.['device-info'];
  const deviceDataV3 = data?.rawData?.device;
  const breakMagicWords = resolveSupplySourcePodDetailsResolver(data);
  const fullContentMagicWords = resolveSupplySourceFullContentMagicWords(data);
  const contentGeoData = data?.rawData?.geo;

  return [
    { label: FIELDS_NAMES.TIMESTAMP, value: timestampIntegrators(data?.[CREATED_TIMESTAMP]) },
    { label: FIELDS_NAMES.REQUEST_TYPE, value: REQUEST_TYPE_MAPPING[CAS] },
    { label: FIELDS_NAMES.REQUEST_ID, value: '-' }, //Hidden until the BE part is ready
    { label: FIELDS_NAMES.SESSION_ID, value: data?.rawData?.['ad-session-id'] },
    { label: FIELDS_NAMES.EXPERIMENT_ID, value: data?.rawData?.['experiment-id'] },
    { label: FIELDS_NAMES.APP_NAME, value: data?.rawData?.app || '' },
    { label: FIELDS_NAMES.ENTITLEMENT, value: contentDataV2?.entitlement || contentDataV3?.entitlement },
    { label: FIELDS_NAMES.PUBLISHER, value: contentDataV2?.publisher || contentDataV3?.publisher },
    { label: FIELDS_NAMES.DISTRIBUTOR, value: contentDataV2?.distributor || contentDataV3?.distributor },
    {
      label: FIELDS_NAMES.DISTRIBUTION_PARTNER,
      value: contentDataV2?.['distribution-partner'] || contentDataV3?.['distribution-partner'],
    },
    { label: FIELDS_NAMES.PLAYBACK_TYPE, value: contentDataV2?.vdm || contentDataV3?.['playback-type'] },
    { label: FIELDS_NAMES.ENTITY_TYPE, value: contentDataV3?.['entity-type'] },
    { label: FIELDS_NAMES.DEVICE_NAME, value: deviceDataV2?.device || deviceDataV3?.name },
    { label: FIELDS_NAMES.BRAND, value: deviceDataV2?.['distribution-partner'] || deviceDataV3?.brand },
    { label: FIELDS_NAMES.FORM_FACTOR, value: deviceDataV2?.['distributor-platform'] || deviceDataV3?.['form-factor'] },
    { label: FIELDS_NAMES.DEVICE_CATEGORY, value: deviceDataV2?.category || deviceDataV3?.category },
    { label: FIELDS_NAMES.IPV6, value: deviceDataV3?.['ip-v6-address'] },
    { label: FIELDS_NAMES.IPV4, value: deviceDataV2?.['ip-address'] || deviceDataV3?.['ip-v4-address'] },
    { label: FIELDS_NAMES.IFA, value: deviceDataV3?.['ad-id'] },
    { label: FIELDS_NAMES.IFA_TYPE, value: deviceDataV3?.['ifa-type'] },
    { label: FIELDS_NAMES.DEVICE_USER_AGENT, value: deviceDataV2?.['user-agent'] || deviceDataV3?.['user-agent'] },
    {
      label: FIELDS_NAMES.LIMITED_AD_TRACKING,
      value: deviceDataV2?.['device-dnt'].toString() || deviceDataV3?.['limit-ad-tracking'],
    },
    { ...breakMagicWords },
    { ...fullContentMagicWords },
    { label: FIELDS_NAMES.COUNTRY, value: contentGeoData?.country || '-' },
    { label: FIELDS_NAMES.STATE, value: contentGeoData?.state || '-' },
    { label: FIELDS_NAMES.CITY, value: contentGeoData?.city || '-' },
    { label: FIELDS_NAMES.POSTAL_CODE, value: contentGeoData?.['postal-code'] || '-' },
    { label: FIELDS_NAMES.DMA_CODE, value: contentGeoData?.['dma-code'] || '-' },
    { label: FIELDS_NAMES.TIME_ZONE, value: contentGeoData?.['time-zone'] || '-' },
  ];
};
const dasSupplySourceSummary = (data) => {
  const contentDataV2 = data?.rawData?.['content-info'];
  const contentDataV3 = data?.rawData?.content;
  const deviceDataV2 = data?.rawData?.['device-info'];
  const deviceDataV3 = data?.rawData?.device;
  const breakMagicWords = resolveSupplySourcePodDetailsResolver(data);
  const fullContentMagicWords = resolveSupplySourceFullContentMagicWords(data);
  const contentGeoData = data?.rawData?.geo;

  return [
    { label: FIELDS_NAMES.TIMESTAMP, value: timestampIntegrators(data?.[CREATED_TIMESTAMP]) },
    { label: FIELDS_NAMES.REQUEST_TYPE, value: REQUEST_TYPE_MAPPING[DAS] },
    { label: FIELDS_NAMES.REQUEST_ID, value: '-' }, //Hidden until the BE part is ready
    { label: FIELDS_NAMES.SESSION_ID, value: data?.rawData?.['ad-session-id'] },
    { label: FIELDS_NAMES.EXPERIMENT_ID, value: data?.rawData?.['experiment-id'] },
    { label: FIELDS_NAMES.APP_NAME, value: data?.rawData?.app || '' },
    { label: FIELDS_NAMES.ENTITLEMENT, value: contentDataV2?.entitlement || contentDataV3?.entitlement },
    { label: FIELDS_NAMES.PUBLISHER, value: contentDataV2?.publisher || contentDataV3?.publisher },
    { label: FIELDS_NAMES.DISTRIBUTOR, value: contentDataV2?.distributor || contentDataV3?.distributor },
    {
      label: FIELDS_NAMES.DISTRIBUTION_PARTNER,
      value: contentDataV2?.['distribution-partner'] || contentDataV3?.['distribution-partner'],
    },
    { label: FIELDS_NAMES.PLAYBACK_TYPE, value: contentDataV2?.vdm || contentDataV3?.['playback-type'] },
    { label: FIELDS_NAMES.ENTITY_TYPE, value: contentDataV3?.['entity-type'] },
    { label: FIELDS_NAMES.DEVICE_NAME, value: deviceDataV2?.device || deviceDataV3?.name },
    { label: FIELDS_NAMES.BRAND, value: deviceDataV2?.['distribution-partner'] || deviceDataV3?.brand },
    { label: FIELDS_NAMES.FORM_FACTOR, value: deviceDataV2?.['distributor-platform'] || deviceDataV3?.['form-factor'] },
    { label: FIELDS_NAMES.DEVICE_CATEGORY, value: deviceDataV2?.category || deviceDataV3?.category },
    { label: FIELDS_NAMES.IPV6, value: deviceDataV3?.['ip-v6-address'] },
    { label: FIELDS_NAMES.IPV4, value: deviceDataV2?.['ip-address'] || deviceDataV3?.['ip-v4-address'] },
    { label: FIELDS_NAMES.IFA, value: deviceDataV3?.['ad-id'] },
    { label: FIELDS_NAMES.IFA_TYPE, value: deviceDataV3?.['ifa-type'] },
    { label: FIELDS_NAMES.DEVICE_USER_AGENT, value: deviceDataV2?.['user-agent'] || deviceDataV3?.['user-agent'] },
    {
      label: FIELDS_NAMES.LIMITED_AD_TRACKING,
      value: deviceDataV2?.['device-dnt'].toString() || deviceDataV3?.['limit-ad-tracking'],
    },
    { ...breakMagicWords },
    { ...fullContentMagicWords },
    { label: FIELDS_NAMES.COUNTRY, value: contentGeoData?.country || '-' },
    { label: FIELDS_NAMES.STATE, value: contentGeoData?.state || '-' },
    { label: FIELDS_NAMES.CITY, value: contentGeoData?.city || '-' },
    { label: FIELDS_NAMES.POSTAL_CODE, value: contentGeoData?.['postal-code'] || '-' },
    { label: FIELDS_NAMES.DMA_CODE, value: contentGeoData?.['dma-code'] || '-' },
    { label: FIELDS_NAMES.TIME_ZONE, value: contentGeoData?.['time-zone'] || '-' },
  ];
};

const supplySourceMap = {
  [VDMS]: vdmsSupplySourceSummary,
  [DSS]: dssSupplySourceSummary,
  [CAS]: casSupplySourceSummary,
  [DAS]: dasSupplySourceSummary,
};

const resolveSupplySourceSummary = (type, data) => {
  const fn = supplySourceMap[type];
  return fn ? fn(data) : DEFAULT_SUPPLY_SOURCE_SUMMARY;
};

export const useSupplySourceSummaryResolver = (type, data) => {
  return useMemo(() => resolveSupplySourceSummary(type, data), [type, data]);
};
